import type { Editor } from '@tiptap/react';
import { MenuType } from '../types/extension-config';

/**
 * Gets the menu configuration for a specific extension
 * @param editor - The Tiptap editor instance
 * @param extensionName - The name of the extension
 * @returns The menu configuration for the extension, or null if not found
 */
export function getExtensionMenuConfig(
  editor: Editor | null,
  extensionName: string
): { menus?: MenuType[] } | null {
  if (!editor) {
    return null;
  }

  const extension = editor.extensionManager.extensions.find(
    (ext) => ext.name === extensionName
  );

  if (!extension) {
    return null;
  }

  // Return the menu configuration from the extension's options
  return extension.options.menus ? { menus: extension.options.menus } : null;
}

/**
 * Checks if an extension should be available in a specific menu
 * @param editor - The Tiptap editor instance
 * @param extensionName - The name of the extension
 * @param menuType - The menu type to check
 * @returns True if the extension should be available in the menu
 */
export function isExtensionAvailableInMenu(
  editor: Editor | null,
  extensionName: string,
  menuType: MenuType
): boolean {
  const config = getExtensionMenuConfig(editor, extensionName);

  // If no menu configuration is specified, assume it's available in all menus
  if (!config || !config.menus) {
    return true;
  }

  return config.menus.includes(menuType);
}
